import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { type ReactNode, useEffect } from "react";
import { EXTERNAL_SESSION_KEY, type ExternalSession, externalSession } from "../../external/web/session.ts";
import { tenantPath } from "../tenant-paths.ts";
import { useAppPathname, useTenantSlug } from "./app-pathname.ts";
import { buildLoginUrl, isSessionRedirectSuppressed } from "./auth-redirect.ts";
import { RequestError } from "./client.ts";
import { FailureReportPrompt } from "./FailureReportPrompt.tsx";
import { GateLoading, LoadErrorCard, NoAccessHereCard, RetryCard, UnavailableCard } from "./GateCards.tsx";
import { RouteContent } from "./RouteContent.tsx";
import { signOutAndLeave } from "./sign-out.ts";
import { clearSignInAttempt, decideGate, type GateView, markSignInAttempt, readSignInAttempt, retryReason } from "./tenant-gate.ts";

const startSignIn = (slug: string): void => {
  markSignInAttempt(slug, Date.now());
  window.location.replace(buildLoginUrl(window.location));
};

const signInParams = (): { loginError: string | null; signedOut: boolean } => {
  const params = new URLSearchParams(window.location.search);
  return { loginError: params.get("login_error"), signedOut: params.has("signed_out") };
};

const useGateView = (slug: string, session: UseQueryResult<ExternalSession>): GateView => {
  return decideGate({
    slug,
    status: session.status,
    httpStatus: session.error instanceof RequestError ? session.error.status : null,
    sessionSlug: session.data?.tenantSlug ?? null,
    ...signInParams(),
    lastAttemptAt: readSignInAttempt(slug),
    now: Date.now(),
    redirectSuppressed: isSessionRedirectSuppressed(),
  });
};

const gateCard = (view: GateView, slug: string, session: UseQueryResult<ExternalSession>): ReactNode => {
  const signOut = (): void => void signOutAndLeave(slug);
  switch (view) {
    case "unavailable":
      return <UnavailableCard onSignOut={signOut} />;
    case "no-access-here":
      return <NoAccessHereCard onGo={() => window.location.assign(tenantPath(session.data?.tenantSlug ?? slug))} onSignOut={signOut} />;
    case "retry":
      return <RetryCard reason={retryReason(signInParams())} onSignIn={() => startSignIn(slug)} />;
    case "error":
      return <LoadErrorCard onRetry={() => void session.refetch()} />;
    default:
      return <GateLoading />;
  }
};

// Decides what a visitor of /<slug>/... sees: the page, a sign-in redirect, or an explanatory card.
export const TenantGate = ({ header, footer, titleCount, children }: { header?: ReactNode; footer?: ReactNode; titleCount?: number; children: ReactNode }): ReactNode => {
  const slug = useTenantSlug();
  const pathname = useAppPathname();
  const session = useQuery({ queryKey: EXTERNAL_SESSION_KEY, queryFn: ({ signal }) => externalSession(signal) });
  const view = useGateView(slug, session);

  useEffect(() => {
    if (view === "redirect") startSignIn(slug);
    // A completed sign-in must not suppress the automatic redirect when the session later expires.
    if (view === "render") clearSignInAttempt(slug);
  }, [view, slug]);

  if (view === "render") {
    return (
      <>
        {header}
        <RouteContent pathname={pathname} titleCount={titleCount}>
          <FailureReportPrompt />
          {children}
        </RouteContent>
        {footer}
      </>
    );
  }
  return (
    <>
      <div className="app-shell-header">
        <header className="app-header">
          <span className="app-header__brand">Linear Portal</span>
        </header>
      </div>
      <RouteContent pathname={pathname}>{gateCard(view, slug, session)}</RouteContent>
    </>
  );
};
